import { faLink } from "@fortawesome/free-solid-svg-icons/faLink";
import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { CopyButton } from "../../../../components/CopyButton";
import { Flex } from "../../../../components/Flex";
import { Info } from "../../../../components/Info";
import { FontSize, Text } from "../../../../components/Text";
import { useStepContext } from "../../../../components/stepper/StepperContext";
import { useStepperRegisterValidation } from "../../../../components/stepper/useStepperRegisterValidation";
import { Integration } from "../../../../model/integration/Integration";
import { IntegrationKind } from "../../../../model/integration/types/IntegrationKind";
import { IntegrationConfigFacebook } from "../../../../model/integration/types/config/IntegrationConfigFacebook";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Breakpoints } from "../../../../theme/Breakpoints";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { AtiraToast } from "../../../../utils/AtiraToast";

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  width: 80%;
  align-self: flex-start;
`;

const Title = styled(Text)`
  font-size: ${FontSize.l};
  color: ${({ theme }) => theme.main};
  font-weight: bold;
  margin-bottom: ${Spacing.s};
`;

const StyledButton = styled(Button)`
  width: 13rem;
  height: 2.3rem;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const StyledCopyButton = styled(CopyButton)`
  margin: 0;
  padding: 0;
`;

const ResponseWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  margin: 2rem auto;

  @media (min-width: ${Breakpoints.PHONE}) {
    width: 60%;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    width: 40%;
  }
`;

const KeyContainer = styled(Flex)`
  flex-direction: column;
  border-radius: ${Rounded.lg};
  width: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-color: ${({ theme }) => theme.main};
    opacity: 0.1;
    z-index: -1;
  }
`;

const KeyTitle = styled(Text)`
  font-weight: bold;
  text-align: center;
  padding: ${Spacing.s} ${Spacing.m};
  background-color: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.white};
`;

export const IntegrationFaceookInitiateIntegrationStep: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation(undefined, {
    keyPrefix: "integration.add-modal.facebook.modal.step.2.content",
  });
  const { currentStep } = useStepContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const userIntegrations = useAppSelector(
    integrationSliceSelectors.selectUserIntegrations,
  );
  const facebookIntegration = userIntegrations.find(
    (integration) => integration.kind === IntegrationKind.FACEBOOK,
  ) as Integration<IntegrationConfigFacebook> | undefined;

  const canGoNext = useCallback(() => {
    return Boolean(facebookIntegration);
  }, [facebookIntegration]);
  useStepperRegisterValidation(currentStep, canGoNext);

  const onCreateFacebookIntegration = async () => {
    try {
      setLoading(true);
      await dispatch(
        integrationActions.createIntegration({
          userId,
          kind: IntegrationKind.FACEBOOK,
          data: { fieldsResolution: {} },
        }),
      ).unwrap();
      await dispatch(
        integrationActions.getUserIntegrations({ userId }),
      ).unwrap();
      AtiraToast.success(
        t("integration.add-modal.facebook.modal.step.2.create.success"),
      );
    } catch (e) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Title>{t("title")}</Title>

      <Text>
        <Trans
          i18nKey={
            "integration.add-modal.facebook.modal.step.2.content.description"
          }
          components={[<strong />]}
        />
      </Text>

      {!facebookIntegration ? (
        <StyledButton
          onClick={onCreateFacebookIntegration}
          loading={loading}
          icon={faLink}
        >
          {t("initiate.button")}
        </StyledButton>
      ) : (
        <Info info={t("already.initiated")} />
      )}

      {facebookIntegration ? (
        <ResponseWrapper>
          <KeyContainer>
            <KeyTitle>{t("secretKey")}</KeyTitle>

            <Flex
              gap="s"
              padding="m"
              alignItems="center"
              justifyContent="center"
            >
              <Text align="center" fontWeight={"bold"}>
                {`${facebookIntegration?.config?.secret?.slice(0, 5)}...${facebookIntegration?.config?.secret?.slice(-5)}`}
              </Text>

              <StyledCopyButton
                tooltipText={facebookIntegration?.config?.secret}
                copy={facebookIntegration?.config?.secret}
                iconColor="main"
              />
            </Flex>
          </KeyContainer>
        </ResponseWrapper>
      ) : null}
    </Container>
  );
};
